import config from "../config";
import CryptoJS from "crypto-js";

let cryptoKey: CryptoKey | null;
let envCryptoKey: CryptoKey | null;
let ecbPublicKey: any;

let encryptionConfig: any = {
  encryptedFields: ["email", "userName", "lastName", "firstName"],
  isEncryptionEnabled: false,
};

const base64ToArrayBuffer = (base64: string) => {
  const binaryString = atob(base64);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes;
};

const arrayBufferToHex = (buffer: any) => {
  return Array.from(new Uint8Array(buffer))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
};

const setEnvCryptoKey = async () => {
  let keyBytes: any;
  if (config.publicKey.length > 32) {
    const msgBuffer = new TextEncoder().encode(config.publicKey);
    const hashBuffer = await crypto.subtle.digest("SHA-256", msgBuffer);
    keyBytes = new Uint8Array(hashBuffer);
  } else {
    keyBytes = new TextEncoder().encode(config.publicKey.replace(/-/g, ""));
  }

  envCryptoKey = await crypto.subtle.importKey(
    "raw",
    keyBytes,
    { name: "AES-GCM" },
    false,
    ["encrypt", "decrypt"]
  );
};

const decryptKey = async () => {
  let decryptedData: any;
	try {
		const encryptedData = base64ToArrayBuffer(encryptionConfig.encryptionKey);
		const iv = encryptedData.slice(0, 12);
		const cipherText = encryptedData.slice(12);
	
		decryptedData = await crypto.subtle.decrypt(
			{
				name: "AES-GCM",
				iv: iv,
			},
			envCryptoKey as CryptoKey,
			cipherText
		);
	} catch (e) {
		throw new Error("Decryption failed");
	}
  return new TextDecoder().decode(decryptedData);
}

const setCryptoKey = async (config = {}) => {
  encryptionConfig = config;
  if (!encryptionConfig.isEncryptionEnabled) {
    return;
  }
  await setEnvCryptoKey();
  const hexKey = await decryptKey();

  let keyBytes: any;
  if (hexKey.length > 32) {
    const msgBuffer = new TextEncoder().encode(hexKey);
    const hashBuffer = await crypto.subtle.digest("SHA-256", msgBuffer);
    keyBytes = new Uint8Array(hashBuffer);
  } else {
    keyBytes = new TextEncoder().encode(hexKey.replace(/-/g, ""));
  }
  ecbPublicKey = CryptoJS.enc.Hex.parse(arrayBufferToHex(keyBytes));  

  cryptoKey = await crypto.subtle.importKey(
    "raw",
    keyBytes,
    { name: "AES-GCM" },
    false,
    ["encrypt", "decrypt"]
  );
};

const encryptData = async (plainText: string, isDeterministic = true) => {
  if (!encryptionConfig.isEncryptionEnabled) {
    return plainText;
  }
  if (!cryptoKey) {
    await setCryptoKey();
  }
  const data = new TextEncoder().encode(plainText);

  const iv = isDeterministic
    ? new Uint8Array(12)
    : crypto.getRandomValues(new Uint8Array(12));

  const encryptedData = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv },
    cryptoKey as CryptoKey,
    data
  );
	return btoa(String.fromCharCode(...iv, ...new Uint8Array(encryptedData)))
};

export const ecbDecryptData = async (ciphertext: string) => {
  if (!encryptionConfig.isEncryptionEnabled) {
    return ciphertext;
  }

  let decryptedData: any;
  let isError = false;
  let decryptedText = "";

  try {
    if (!ecbPublicKey) {
      await setCryptoKey();
    }
    const val = CryptoJS.enc.Hex.stringify(
      CryptoJS.enc.Base64.parse(ciphertext)
    );
    decryptedData = CryptoJS.AES.decrypt(
      {
        ciphertext: CryptoJS.enc.Hex.parse(val),
      } as CryptoJS.lib.CipherParams,
      ecbPublicKey,
      {
        mode: CryptoJS.mode.ECB,
        padding: CryptoJS.pad.Pkcs7,
      }
    );
    decryptedText = decryptedData.toString(CryptoJS.enc.Utf8);
  } catch (e) {
    isError = true;
  }

  return isError || !decryptedText ? ciphertext : decryptedText;
};

const decryptData = async (encryptedText: string) => {
  if (!encryptionConfig.isEncryptionEnabled) {
    return encryptedText;
  }
	let decryptedData: any;
	let isError = false;
	try {
		if (!cryptoKey) {
			await setCryptoKey();
		}
	
		const encryptedData = base64ToArrayBuffer(encryptedText);
		const iv = encryptedData.slice(0, 12);
		const cipherText = encryptedData.slice(12);
	
		decryptedData = await crypto.subtle.decrypt(
			{
				name: "AES-GCM",
				iv: iv,
			},
			cryptoKey as CryptoKey,
			cipherText
		);
	} catch (e) {
		isError = true;
	}
  return isError ? encryptedText : new TextDecoder().decode(decryptedData);
};

export const ecbEncryptData = async (plaintext: string) => {
  if (!encryptionConfig.isEncryptionEnabled) {
    return plaintext;
  }
  let encryptedData: any;
  let isError = false;

  try {
    if (!ecbPublicKey) {
      await setCryptoKey();
    }

    encryptedData = CryptoJS.AES.encrypt(
      CryptoJS.enc.Utf8.parse(plaintext),
      ecbPublicKey,
      {
        mode: CryptoJS.mode.ECB,
        padding: CryptoJS.pad.Pkcs7,
      }
    );
  } catch (e) {
    isError = true;
  }
  return isError
    ? plaintext
    : CryptoJS.enc.Base64.stringify(encryptedData.ciphertext);
};

export const HelperMethods = {
  base64ToArrayBuffer,
  arrayBufferToHex,
};

export const CryptoMethods = {
  setCryptoKey,
  encryptData,
  decryptData,
  ecbDecryptData,
  ecbEncryptData,
};
